import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PendingApprovalNotice } from '@/features/approvals';
import { usePermissions } from '@/features/auth';
import { ApiError } from '@/shared/api/api-error';
import { ErrorState } from '@/shared/components/error-state';
import { StatusBadge } from '@/shared/components/status-badge';
import { formatDateTime } from '@/shared/lib/format';

import { useFxHistory, useRequestFxOverride, useRevokeFxOverride } from '../api/pricing.queries';
import type { FxOverride } from '../types/pricing.types';

const DAYS = 30;
const REASON_CLASS =
  'border-input bg-background focus-visible:ring-ring w-full rounded-md border px-2 py-1.5 text-xs focus-visible:ring-2 focus-visible:outline-none';

const STATE_VARIANT: Record<FxOverride['state'], 'success' | 'neutral' | 'warning'> = {
  live: 'success',
  expired: 'neutral',
  revoked: 'warning',
};

/**
 * A currency's rates: the feed over the last 30 days and manual rates. With
 * fx.override.request, ask for a manual rate with an expiry (an Admin approves
 * it) or end the one in force. A rate belongs to the currency, so it changes
 * prices in every country that uses it. Mount only while open.
 */
export function FxHistoryDialog({
  countryId,
  currencyCode,
  onClose,
}: {
  countryId: number;
  currencyCode: string;
  onClose: () => void;
}) {
  const { can } = usePermissions();
  const history = useFxHistory(countryId, currencyCode, DAYS);
  const live = history.data?.overrides.find((o) => o.state === 'live') ?? null;
  const canRequest = can('fx.override.request');

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-base">{currencyCode} exchange rate</DialogTitle>
          <DialogDescription className="text-xs">
            Units of {currencyCode} per 1 USD. A rate applies to every country that prices in{' '}
            {currencyCode}.
          </DialogDescription>
        </DialogHeader>

        {history.isLoading ? (
          <Skeleton className="h-48" />
        ) : history.error || !history.data ? (
          <ErrorState error={history.error} onRetry={() => void history.refetch()} />
        ) : (
          <div className="space-y-5 text-xs">
            {live && (
              <p
                role="status"
                className="border-status-warning-border bg-status-warning-bg text-status-warning rounded-md border p-2"
              >
                Manual rate <strong>{live.rate}</strong> is in force until{' '}
                {formatDateTime(live.expiresAtUtc)} — prices use it instead of the feed.
              </p>
            )}
            {history.data.pendingApprovalId != null && (
              <PendingApprovalNotice
                approvalId={history.data.pendingApprovalId}
                type="FxRateOverride"
              >
                A manual rate is waiting for an Admin&apos;s approval.
              </PendingApprovalNotice>
            )}

            {canRequest && (
              <OverrideActions
                countryId={countryId}
                currencyCode={currencyCode}
                maximumHours={history.data.maximumOverrideHours}
                canRequest={history.data.pendingApprovalId == null}
                live={live}
              />
            )}

            <section className="space-y-2">
              <h3 className="font-medium">Manual rates</h3>
              {history.data.overrides.length === 0 ? (
                <p className="text-muted-foreground">No manual rate has been used.</p>
              ) : (
                <ul className="space-y-1.5" aria-label="Manual rates">
                  {history.data.overrides.map((item) => (
                    <li key={item.id} className="flex flex-wrap items-center justify-between gap-2">
                      <span>
                        <span className="font-mono tabular-nums">{item.rate}</span> ·{' '}
                        {formatDateTime(item.validFromUtc)} → {formatDateTime(item.expiresAtUtc)} ·{' '}
                        {item.reason}
                      </span>
                      <StatusBadge variant={STATE_VARIANT[item.state]}>{item.state}</StatusBadge>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="space-y-2">
              <h3 className="font-medium">Feed, last {DAYS} days</h3>
              {history.data.feedRates.length === 0 ? (
                <p className="text-muted-foreground">No rate from the feed in this period.</p>
              ) : (
                <div className="border-border max-h-64 overflow-y-auto rounded-md border">
                  <Table className="text-xs" aria-label="Feed rates">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fetched</TableHead>
                        <TableHead className="text-end">Rate</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {history.data.feedRates.map((rate) => (
                        <TableRow key={rate.fetchedAtUtc}>
                          <TableCell>{formatDateTime(rate.fetchedAtUtc)}</TableCell>
                          <TableCell className="text-end tabular-nums">{rate.rate}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function OverrideActions({
  countryId,
  currencyCode,
  maximumHours,
  canRequest,
  live,
}: {
  countryId: number;
  currencyCode: string;
  maximumHours: number;
  canRequest: boolean;
  live: FxOverride | null;
}) {
  const [mode, setMode] = useState<'request' | 'revoke' | null>(null);
  const [rate, setRate] = useState('');
  const [hours, setHours] = useState('24');
  const [reason, setReason] = useState('');
  const request = useRequestFxOverride(countryId, currencyCode);
  const revoke = useRevokeFxOverride(countryId, currencyCode);

  const parsedRate = Number(rate);
  const parsedHours = Number(hours);
  const validRequest =
    Number.isFinite(parsedRate) &&
    parsedRate > 0 &&
    Number.isInteger(parsedHours) &&
    parsedHours >= 1 &&
    parsedHours <= maximumHours &&
    reason.trim().length >= 3;
  const done = () => {
    setMode(null);
    setReason('');
  };
  const error = mode === 'revoke' ? revoke.error : request.error;

  if (mode === null) {
    return (
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={!canRequest}
          onClick={() => setMode('request')}
        >
          Request manual rate
        </Button>
        {live && (
          <Button size="sm" variant="outline" onClick={() => setMode('revoke')}>
            End manual rate
          </Button>
        )}
      </div>
    );
  }

  return (
    <form
      className="border-border space-y-3 rounded-md border p-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (mode === 'revoke') revoke.mutate(reason.trim(), { onSuccess: done });
        else if (validRequest)
          request.mutate(
            { rate: parsedRate, durationHours: parsedHours, reason: reason.trim() },
            { onSuccess: done },
          );
      }}
    >
      <p className="font-medium">
        {mode === 'revoke' ? 'End the manual rate now?' : 'Request a manual rate'}
      </p>
      {mode === 'request' && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="fx-rate" className="text-xs">
              Rate ({currencyCode} per 1 USD)
            </Label>
            <Input
              id="fx-rate"
              type="number"
              min={0}
              step="any"
              value={rate}
              onChange={(event) => setRate(event.target.value)}
              className="h-8 text-xs"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fx-hours" className="text-xs">
              In force for (hours, at most {maximumHours})
            </Label>
            <Input
              id="fx-hours"
              type="number"
              min={1}
              max={maximumHours}
              step={1}
              value={hours}
              onChange={(event) => setHours(event.target.value)}
              className="h-8 text-xs"
            />
          </div>
        </div>
      )}
      <div className="space-y-1.5">
        <Label htmlFor="fx-reason" className="text-xs">
          Reason <span className="text-muted-foreground font-normal">(required)</span>
        </Label>
        <textarea
          id="fx-reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={2}
          maxLength={500}
          className={REASON_CLASS}
        />
      </div>
      {mode === 'request' && (
        <p className="text-muted-foreground">
          An Admin must approve it. It applies from approval for the hours above, then the feed
          takes over again.
        </p>
      )}
      {error instanceof ApiError && (
        <p role="alert" className="text-destructive">
          {error.fieldErrors?.DurationHours ?? error.message}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="outline" onClick={done}>
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          variant={mode === 'revoke' ? 'destructive' : 'default'}
          disabled={
            request.isPending ||
            revoke.isPending ||
            (mode === 'request' ? !validRequest : reason.trim().length < 3)
          }
        >
          {mode === 'revoke' ? 'End manual rate' : 'Send for approval'}
        </Button>
      </div>
    </form>
  );
}
